import React, { useEffect, useRef, useState } from 'react';
import useLang from '../../i18n/useLang';
import { useInView, usePrefersReducedMotion } from '../site/motion';

/*
 * An isometric production line wired to SAP through the plant's own system: products ride the conveyor,
 * the vision station scans each one, and data rises from the machines into the on-site system and on to SAP.
 * World units: x runs down-right, y down-left, z up. P() projects a world point into the viewBox.
 */

const COS = 0.8660254;
const SIN = 0.5;
const OX = 196;
const OY = 368;
const VB_W = 790;
const VB_H = 730;

const P = (x, y, z = 0) => [OX + (x - y) * COS, OY + (x + y) * SIN - z];
const pts = (...ps) => ps.map(([a, b]) => `${a.toFixed(1)},${b.toFixed(1)}`).join(' ');
const line = (...ps) => ps.map(([a, b], i) => `${i ? 'L' : 'M'}${a.toFixed(1)} ${b.toFixed(1)}`).join(' ');
const at = ([a, b]) => ({ left: `${(a / VB_W) * 100}%`, top: `${(b / VB_H) * 100}%` });

// Face colours: [top, front-left (y+d), front-right (x+w), top-edge highlight]
const FLOOR = ['#EEF2F6', '#D9E0E8', '#C6D0DB', '#FFFFFF'];
const BODY = ['#FFFFFF', '#E8EDF2', '#D3DBE4', '#FFFFFF'];
const CABINET = ['#F6F8FA', '#E1E7ED', '#CDD5DE', '#FFFFFF'];
const BENCH = ['#2A3E53', '#A3B2C2', '#8D9EB0', '#4A607A'];
const STEEL = ['#2A4562', '#1C3450', '#12263C', '#3D5A7A'];
const PRODUCT = ['#CFEFBE', '#A3DC84', '#82C95E', '#E9F8E1'];
const HUB = ['#1B3149', '#12263C', '#0B1B2E', '#61C435'];
const SERVER = ['#FFFFFF', '#CFDFF2', '#A9C2E2', '#FFFFFF'];

function Box({ x, y, z = 0, w, d, h, c }) {
  const t1 = P(x, y, z + h);
  const t2 = P(x + w, y, z + h);
  const t3 = P(x + w, y + d, z + h);
  const t4 = P(x, y + d, z + h);
  const b2 = P(x + w, y, z);
  const b3 = P(x + w, y + d, z);
  const b4 = P(x, y + d, z);
  return (
    <g>
      <polygon points={pts(t4, t3, b3, b4)} fill={c[1]} />
      <polygon points={pts(t2, t3, b3, b2)} fill={c[2]} />
      <polygon points={pts(t1, t2, t3, t4)} fill={c[0]} />
      <polyline points={pts(t4, t1, t2)} fill="none" stroke={c[3]} strokeWidth="1" strokeLinejoin="round" opacity=".9" />
    </g>
  );
}

// A flat quad on a face, given four world points.
const Quad = ({ p, fill, opacity, className }) => <polygon points={pts(...p.map((q) => P(...q)))} fill={fill} opacity={opacity} className={className} />;

const ROUTES = {
  plc: line(P(54, 75, 90), P(54, 75, 165), P(54, -150, 165), P(230, -150, 165)),
  machine: line(P(176, 72, 96), P(176, 72, 165), P(176, -128, 165), P(230, -128, 165)),
  vision: line(P(306, 147, 84), P(306, 147, 165), P(306, -110, 165)),
  sap: line(P(280, -140, 180), P(280, -140, 272), P(280, -260, 272)),
};

function Packet({ route, dur, begin, still }) {
  if (still) return null;
  return (
    <g>
      <circle r="7" fill="#61C435" opacity="0">
        <animateMotion dur={dur} begin={begin} repeatCount="indefinite" path={route} keyPoints="0;1;1" keyTimes="0;.78;1" calcMode="linear" />
        <animate attributeName="opacity" dur={dur} begin={begin} repeatCount="indefinite" values="0;.25;.25;0;0" keyTimes="0;.08;.7;.78;1" />
      </circle>
      <circle r="3.4" fill="#61C435" opacity="0">
        <animateMotion dur={dur} begin={begin} repeatCount="indefinite" path={route} keyPoints="0;1;1" keyTimes="0;.78;1" calcMode="linear" />
        <animate attributeName="opacity" dur={dur} begin={begin} repeatCount="indefinite" values="0;1;1;0;0" keyTimes="0;.08;.7;.78;1" />
      </circle>
    </g>
  );
}

function Callout({ style, className = '', title, detail, mono = true }) {
  return (
    <div className={`absolute z-10 rounded-xl bg-white/95 px-3 py-2 shadow-card ring-1 ring-fog-200 backdrop-blur ${className}`} style={style}>
      <div dir="auto" className="whitespace-nowrap text-[12.5px] font-semibold leading-tight text-ink">
        {title}
      </div>
      {detail && (
        <div dir="ltr" className={`mt-0.5 whitespace-nowrap text-start text-[11px] leading-tight text-ink-500 ${mono ? 'font-mono' : ''}`}>
          {detail}
        </div>
      )}
    </div>
  );
}

const STATIC_PRODUCTS = [0.08, 0.36, 0.62, 0.88];

export default function PlantScene() {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const svgRef = useRef(null);
  const reduced = usePrefersReducedMotion();
  const visible = useInView(ref, { once: false, threshold: 0.05, rootMargin: '0px' });
  const [count, setCount] = useState(1240);
  const running = visible && !reduced;

  // Pause every loop while the scene is off screen.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !svg.pauseAnimations) return;
    if (running) svg.unpauseAnimations();
    else svg.pauseAnimations();
  }, [running]);

  // One more confirmed piece each time a product clears the vision station.
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setCount((c) => (c >= 1299 ? 1240 : c + 1)), 2000);
    return () => clearInterval(id);
  }, [running]);

  const fmt = new Intl.NumberFormat(lang === 'he' ? 'he-IL' : 'en-US');

  return (
    <figure ref={ref} className={`plant-scene relative mx-auto w-full max-w-[760px] ${running ? '' : 'is-paused'}`} aria-labelledby="plant-caption">
      <div dir="ltr" className="relative">
        <svg ref={svgRef} viewBox={`0 0 ${VB_W} ${VB_H}`} className="block h-auto w-full" role="img" aria-labelledby="plant-caption">
          {/* Floor slab with a light grid and a walkway marking */}
          <Box x={0} y={0} z={-16} w={460} d={200} h={16} c={FLOOR} />
          <path
            d={[40, 80, 120, 160].map((y) => line(P(0, y), P(460, y))).join(' ') + ' ' + [40, 80, 120, 160, 200, 240, 280, 320, 360, 400, 440].map((x) => line(P(x, 0), P(x, 200))).join(' ')}
            stroke="#E2E8EE"
            strokeWidth="1"
            fill="none"
          />
          <path d={line(P(8, 188), P(452, 188))} stroke="#F2B53A" strokeWidth="2.2" strokeDasharray="10 8" opacity=".75" />

          {/* PLC cabinet */}
          <Box x={30} y={50} w={48} d={50} h={90} c={CABINET} />
          <polyline points={pts(P(36, 100, 84), P(72, 100, 84), P(72, 100, 6), P(36, 100, 6), P(36, 100, 84))} fill="none" stroke="#C3CDD7" strokeWidth="1" />
          <Quad p={[[40, 100, 70], [68, 100, 70], [68, 100, 52], [40, 100, 52]]} fill="#12263C" />
          <polyline points={pts(P(43, 100, 58), P(48, 100, 63), P(53, 100, 60), P(58, 100, 66), P(64, 100, 62))} fill="none" stroke="#61C435" strokeWidth="1.4" />
          {[42, 50, 58].map((x, i) => {
            const [cx, cy] = P(x, 100, 42);
            return <circle key={x} cx={cx} cy={cy} r="2.3" className={`ps-led ps-led-${i}`} fill={i === 1 ? '#F2B53A' : '#61C435'} />;
          })}
          {[22, 18, 14].map((z) => (
            <path key={z} d={line(P(42, 100, z), P(66, 100, z))} stroke="#C3CDD7" strokeWidth="1.2" />
          ))}

          {/* Processing machine with a stack light */}
          <Box x={110} y={36} w={96} d={70} h={72} c={BODY} />
          <Quad p={[[122, 106, 56], [194, 106, 56], [194, 106, 22], [122, 106, 22]]} fill="#12263C" />
          <Quad p={[[128, 106, 50], [188, 106, 50], [188, 106, 28], [128, 106, 28]]} fill="#1C3450" />
          <path className="ps-glow" d={line(P(132, 106, 39), P(184, 106, 39))} stroke="#61C435" strokeWidth="2" strokeLinecap="round" />
          <Quad p={[[206, 48, 58], [206, 62, 58], [206, 62, 38], [206, 48, 38]]} fill="#C3CDD7" />
          <Box x={171} y={67} w={10} d={10} h={4} z={72} c={STEEL} />
          <g className="ps-stack-green">
            <Box x={172} y={68} z={76} w={8} d={8} h={8} c={['#A6E38A', '#61C435', '#4DA626', '#D5F3C6']} />
          </g>
          <Box x={172} y={68} z={84} w={8} d={8} h={8} c={['#F8D58C', '#E9B44C', '#D49A2E', '#FBE7BF']} />
          <Box x={172} y={68} z={92} w={8} d={8} h={8} c={['#F3B2B4', '#E07A7E', '#C9585D', '#F9D6D7']} />

          {/* Robot at the end of the line */}
          <Box x={424} y={92} w={24} d={24} h={12} c={STEEL} />
          {(() => {
            const shoulder = P(436, 104, 12);
            const elbow = P(436, 104, 52);
            const reach = P(414, 140, 66);
            const grip = P(414, 146, 46);
            return (
              <g>
                <path d={line(shoulder, elbow)} stroke="#1C3450" strokeWidth="9" strokeLinecap="round" />
                <g>
                  {!reduced && (
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      values={`-7 ${elbow[0]} ${elbow[1]}; 9 ${elbow[0]} ${elbow[1]}; -7 ${elbow[0]} ${elbow[1]}`}
                      dur="4s"
                      repeatCount="indefinite"
                      calcMode="spline"
                      keySplines=".45 0 .25 1; .45 0 .25 1"
                    />
                  )}
                  <path d={line(elbow, reach, grip)} stroke="#2A4562" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <circle cx={reach[0]} cy={reach[1]} r="4.5" fill="#DCE3EA" stroke="#2A4562" strokeWidth="2" />
                  <path d={`M${grip[0] - 6} ${grip[1]} h12`} stroke="#2A4562" strokeWidth="3" strokeLinecap="round" />
                </g>
                <circle cx={elbow[0]} cy={elbow[1]} r="5.5" fill="#DCE3EA" stroke="#1C3450" strokeWidth="2" />
              </g>
            );
          })()}

          {/* Vision gantry, back post */}
          <Box x={300} y={114} w={12} d={10} h={72} c={STEEL} />

          {/* Conveyor */}
          <Box x={20} y={128} w={404} d={38} h={20} c={BENCH} />
          <path className="ps-belt" d={line(P(24, 140, 20), P(420, 140, 20))} stroke="#5A7290" strokeWidth="1.5" strokeDasharray="3 9" />
          <path className="ps-belt" d={line(P(24, 154, 20), P(420, 154, 20))} stroke="#5A7290" strokeWidth="1.5" strokeDasharray="3 9" />

          {/* Products */}
          {[0, 1, 2, 3].map((i) => (
            <g
              key={i}
              className="ps-product"
              style={
                reduced
                  ? { transform: `translate(${(STATIC_PRODUCTS[i] * 322.2).toFixed(1)}px, ${(STATIC_PRODUCTS[i] * 186).toFixed(1)}px)` }
                  : { animationDelay: `${-i * 2}s` }
              }
            >
              <Box x={24} y={135} z={20} w={24} d={24} h={20} c={PRODUCT} />
            </g>
          ))}

          {/* Vision gantry: scan beam, front post, beam and camera */}
          <Quad className="ps-scan" p={[[306, 141, 60], [306, 153, 60], [306, 160, 20], [306, 134, 20]]} fill="#61C435" opacity={reduced ? 0.18 : undefined} />
          <Box x={300} y={170} w={12} d={10} h={72} c={STEEL} />
          <Box x={300} y={114} z={72} w={12} d={66} h={12} c={STEEL} />
          <Box x={300} y={141} z={60} w={12} d={12} h={12} c={['#2A4562', '#0F2236', '#0B1B2E', '#3D5A7A']} />
          {(() => {
            const [cx, cy] = P(306, 153, 64);
            return <circle cx={cx} cy={cy} r="2.6" fill="#61C435" className="ps-glow" />;
          })()}

          {/* Data routes from the line into the on-site system, and from there into SAP */}
          {Object.entries(ROUTES).map(([key, d]) => (
            <path key={key} d={d} fill="none" stroke="#61C435" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="1.5 6" opacity=".7" />
          ))}
          <Packet route={ROUTES.plc} dur="3.6s" begin="0.2s" still={reduced} />
          <Packet route={ROUTES.machine} dur="2.8s" begin="0.9s" still={reduced} />
          <Packet route={ROUTES.vision} dur="2s" begin="2s" still={reduced} />
          <Packet route={ROUTES.sap} dur="2s" begin="3.6s" still={reduced} />

          {/* The plant's own system, running on site */}
          <Box x={230} y={-170} z={150} w={100} d={60} h={30} c={HUB} />
          {[242, 252, 262, 272].map((x, i) => {
            const [cx, cy] = P(x, -110, 165);
            return <circle key={x} cx={cx} cy={cy} r="2" fill="#61C435" className={`ps-led ps-led-${i % 3}`} />;
          })}
          <path d={line(P(286, -110, 168), P(320, -110, 168))} stroke="#2A4562" strokeWidth="2" strokeLinecap="round" />
          <path d={line(P(286, -110, 160), P(312, -110, 160))} stroke="#2A4562" strokeWidth="2" strokeLinecap="round" />

          {/* SAP */}
          {[250, 266, 282].map((z) => (
            <Box key={z} x={200} y={-330} z={z} w={120} d={70} h={12} c={SERVER} />
          ))}
          {[250, 266, 282].map((z) =>
            [212, 220, 228].map((x, i) => {
              const [cx, cy] = P(x, -260, z + 6);
              return <circle key={`${z}-${x}`} cx={cx} cy={cy} r="1.8" fill={i === 0 ? '#61C435' : '#9FB4CC'} className={i === 0 ? 'ps-led ps-led-1' : undefined} />;
            })
          )}
        </svg>

        {/* Labels */}
        <Callout
          className="hidden -translate-x-1/2 -translate-y-full sm:block"
          style={at([P(54, 75, 90)[0], P(54, 75, 90)[1] - 26])}
          title={t('home.scene.plc')}
          detail="OPC UA"
        />
        <Callout
          className="hidden -translate-x-1/2 sm:block"
          style={at([P(306, 200, -16)[0], P(306, 200, -16)[1] + 16])}
          title={t('home.scene.vision')}
          detail="REST · Reject = 0"
        />
        <Callout
          className="hidden -translate-x-full -translate-y-1/2 sm:block"
          style={at([P(230, -110, 165)[0] - 16, P(230, -110, 165)[1] + 4])}
          title={t('home.scene.hub')}
          detail="Counter.Good → yield"
        />


        <div className="absolute z-10 hidden w-[218px] -translate-x-full rounded-2xl bg-white p-4 shadow-float ring-1 ring-fog-200 sm:block" style={at([P(200, -260, 290)[0] - 26, 22])}>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[12.5px] font-semibold text-ink">SAP S/4HANA</span>
            <span className="relative flex h-2 w-2" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full rounded-full bg-grovv opacity-60 motion-safe:animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-grovv-600" />
            </span>
          </div>
          <div dir="auto" className="mt-3 text-start text-[12px] text-ink-500">
            {t('home.scene.event')}
          </div>
          <div className="mt-0.5 flex items-baseline gap-1.5" dir="auto">
            <span key={count} className="ps-count inline-block text-[1.6rem] font-bold leading-none tracking-tight text-ink tabular-nums">
              {fmt.format(count)}
            </span>
            <span className="text-[12px] text-ink-500">{t('home.scene.unit')}</span>
          </div>
          <div dir="ltr" className="mt-3 border-t border-fog-100 pt-2 text-start font-mono text-[10.5px] text-ink-500">
            order 1000452 · op 0020
          </div>
        </div>
      </div>

      {/* Phones: the SAP reading sits under the drawing instead of on it */}
      <div className="mt-2 flex items-center justify-between gap-4 rounded-2xl bg-white px-4 py-3 shadow-card ring-1 ring-fog-200 sm:hidden">
        <div className="min-w-0">
          <div className="text-[12.5px] font-semibold text-ink">SAP S/4HANA</div>
          <div dir="auto" className="mt-0.5 text-start text-[12px] text-ink-500">
            {t('home.scene.event')}
          </div>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span key={count} className="ps-count inline-block text-[1.4rem] font-bold leading-none text-ink tabular-nums">
            {fmt.format(count)}
          </span>
          <span className="text-[12px] text-ink-500">{t('home.scene.unit')}</span>
        </div>
      </div>

      <figcaption id="plant-caption" className="mt-3 text-center text-[13px] text-ink-500">
        {t('home.scene.caption')}
      </figcaption>
    </figure>
  );
}
